'use client'

/**
 * Higiene dos anúncios automáticos.
 *
 * NÃO empurra `enable_page_level_ads`. O Auto Ads é ligado no painel do AdSense
 * (por domínio) e o próprio adsbygoogle.js já o inicializa no carregamento. Empurrar
 * de novo daqui gerava em produção `TagError: Only one 'enable_page_level_ads'
 * allowed per page` — o push era descartado e ainda sujava o console.
 *
 * O que sobrou: remover unidades órfãs que o Auto Ads injeta DEPOIS do footer,
 * que criavam scroll fantasma no fim da página.
 */

import { useEffect } from 'react'
import { useAuth } from '@/lib/auth/context'
import { usePathname } from 'next/navigation'
import { shouldShowAds } from '@/lib/ads/should-show-ads'

declare global {
  interface Window {
    adsbygoogle?: unknown[]
  }
}

/**
 * Remove Auto Ads órfãos que caem NO NÍVEL do footer ou abaixo — ninguém rola
 * além do rodapé, então um anúncio ali é só uma faixa vazia quebrando o layout.
 *
 * Busca `.google-auto-placed` em QUALQUER lugar do documento (não só filhos
 * diretos do body): o Auto Ads às vezes injeta dentro do wrapper do chrome,
 * depois do footer, e a versão antiga (`body >`) não pegava.
 */
function stripPostFooterAutoAds() {
  const footer = document.querySelector('footer')
  if (!footer) return
  const footerTop = footer.getBoundingClientRect().top + window.scrollY
  document.querySelectorAll('.google-auto-placed').forEach((el) => {
    // só remove o que está NO NÍVEL do footer ou abaixo; âncora (fixed) e
    // qualquer unidade acima do footer são legítimas e ficam.
    const top = el.getBoundingClientRect().top + window.scrollY
    if (top >= footerTop - 8) el.remove()
  })
}

export function AutoAdsInit() {
  const { user } = useAuth()
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!shouldShowAds(user)) return

    stripPostFooterAutoAds()
    const obs = new MutationObserver(() => stripPostFooterAutoAds())
    obs.observe(document.body, { childList: true })
    const t1 = window.setTimeout(stripPostFooterAutoAds, 1500)
    const t2 = window.setTimeout(stripPostFooterAutoAds, 4000)
    return () => {
      obs.disconnect()
      window.clearTimeout(t1)
      window.clearTimeout(t2)
    }
  }, [pathname, user])

  return null
}
